import { useBoardStore, unsavedOperations } from '../../state/board-store.js'
import { useSessionStore } from '../../state/session-store.js'
import { Button } from '../../components/controls.js'
import styles from './SaveStatus.module.css'

const LABELS = {
  saved: 'Saved',
  saving: 'Saving…',
  unsaved: 'Unsaved',
  failed: 'Save failed',
  conflict: 'Save conflict',
} as const

/** The pill reports what storage has accepted — never more than that. */
export function SaveStatus() {
  const save = useBoardStore((state) => state.save)

  return (
    <span className={`${styles.pill} ${styles[save.status]}`} role="status" data-testid="save-status">
      <span className={styles.dot} />
      {LABELS[save.status]}
      {save.pending > 0 && save.status !== 'saved' ? ` (${save.pending})` : ''}
    </span>
  )
}

function downloadPendingWork(boardId: string, boardName: string): void {
  const operations = unsavedOperations(boardId)
  const blob = new Blob([JSON.stringify({ boardId, boardName, operations }, null, 2)], {
    type: 'application/json',
  })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `unsaved-changes-${boardId.slice(0, 8)}.json`
  anchor.click()
  URL.revokeObjectURL(url)
}

/**
 * Shown when the queue is paused. A conflict never force-overwrites and never merges:
 * the pending work can be downloaded, then reloaded or explicitly discarded.
 */
export function SaveFailureBanner() {
  const save = useBoardStore((state) => state.save)
  const boardId = useBoardStore((state) => state.activeBoardId)
  const boardName = useBoardStore((state) => state.entities?.board.name ?? 'board')
  const retrySave = useBoardStore((state) => state.retrySave)
  const discardUnsaved = useBoardStore((state) => state.discardUnsaved)
  // During a Teamcentric Accounts trial no data leaves the tenant: no download.
  const trial = useSessionStore((state) => state.trial)

  if (!boardId || (save.status !== 'failed' && save.status !== 'conflict')) return null

  const isConflict = save.status === 'conflict'
  return (
    <div className={styles.banner} role="alert">
      <strong>{isConflict ? 'This board changed elsewhere.' : 'Changes could not be saved.'}</strong>
      <span>
        {save.error}
        {isConflict && save.serverRevision !== null
          ? ` Storage is at revision ${save.serverRevision}; this tab is at ${save.revision}.`
          : ''}
      </span>
      <span>Your changes are still on screen and have not been thrown away.</span>
      {trial ? null : <Button onClick={() => downloadPendingWork(boardId, boardName)}>Download unsaved changes</Button>}
      {isConflict ? null : <Button onClick={retrySave}>Retry</Button>}
      <Button variant="danger" onClick={() => void discardUnsaved()}>
        {isConflict ? 'Reload from storage' : 'Discard changes'}
      </Button>
    </div>
  )
}
